import { Injectable } from '@angular/core';
import { environment } from '../../environments/environment';
import { IndexedDbService } from './indexed-db.service';

export enum LogLevel {
  DEBUG = 'debug',
  WARN = 'warn',
  ERROR = 'error',
  NONE = 'none'
}

type LoggingPreferences = {
  [componentName: string]: LogLevel;
};

@Injectable({
  providedIn: 'root'
})
export class LoggingService {
  private componentLogLevels = new Map<string, LogLevel>();
  private logLevel: LogLevel;
  private preferencesReady = false;
  private pendingSave: ReturnType<typeof setTimeout> | null = null;

  // Safe references to native console methods (Bug 6: fallback if main.ts hasn't run)
  private static readonly nativeLog: (...args: any[]) => void =
    (window as any).__originalConsoleLog ?? console.log.bind(console);
  private static readonly nativeWarn: (...args: any[]) => void =
    (window as any).__originalConsoleWarn ?? console.warn.bind(console);
  private static readonly nativeError: (...args: any[]) => void =
    (window as any).__originalConsoleError ?? console.error.bind(console);

  constructor(private indexedDbService: IndexedDbService) {
    this.logLevel = this.getLogLevelFromString(environment.logLevel);
    this.initializeComponentLogLevels();
  }

  private getLogLevelFromString(levelString: string): LogLevel {
    return LogLevel[levelString.toUpperCase() as keyof typeof LogLevel] || LogLevel.NONE;
  }

  private async initializeComponentLogLevels(): Promise<void> {
    try {
      const savedLevels: LoggingPreferences | null = await this.indexedDbService.get('loggingPreferences');
      this.setLogLevelsFromSavedPreferences(savedLevels);
    } catch (error) {
      const savedLevelsString = localStorage.getItem('loggingPreferences');
      const savedLevels: LoggingPreferences = savedLevelsString ? JSON.parse(savedLevelsString) : {};
      this.setLogLevelsFromSavedPreferences(savedLevels);
    }
    this.preferencesReady = true;
  }

  private setLogLevelsFromSavedPreferences(savedLevels: LoggingPreferences | null): void {
    if (savedLevels) {
      Object.entries(savedLevels).forEach(([componentName, level]) => {
        if (Object.values(LogLevel).includes(level)) {
          this.componentLogLevels.set(componentName, level);
        }
      });
    }
  }

  log(level: LogLevel, componentName: string, message: string, ...optionalParams: any[]): void {
    // Before preferences load (~1-5ms on startup), use environment default level so no logs are missed
    const effectiveLogLevel = this.preferencesReady
      ? this.getEffectiveLogLevel(componentName)
      : this.logLevel;
    if (this.shouldLog(level, effectiveLogLevel)) {
      switch (level) {
        case LogLevel.DEBUG:
          this.logWithStyle('color: #4040A6;', level, componentName, message, optionalParams);
          break;
        case LogLevel.WARN:
          this.logWithStyle('color: orange;', level, componentName, message, optionalParams);
          break;
        case LogLevel.ERROR:
          this.logWithStyle('color: red;', level, componentName, message, optionalParams);
          break;
        default:
          this.logWithStyle('', level, componentName, message, optionalParams);
      }
    }
  }

  private logWithStyle(style: string, level: LogLevel, componentName: string, message: string, optionalParams: any[]) {
    const styledMessage = `%c[${level}] ${componentName}: ${message}`;
    // Bug 5 fix: route WARN/ERROR to their native console methods for proper devtools filtering
    switch (level) {
      case LogLevel.WARN:
        LoggingService.nativeWarn(styledMessage, style, ...optionalParams);
        break;
      case LogLevel.ERROR:
        LoggingService.nativeError(styledMessage, style, ...optionalParams);
        break;
      default:
        LoggingService.nativeLog(styledMessage, style, ...optionalParams);
    }
  }

  private getEffectiveLogLevel(componentName: string): LogLevel {
    if (!this.componentLogLevels.has(componentName)) {
      this.componentLogLevels.set(componentName, this.logLevel);
      this.debouncedSave();
    }
    return this.componentLogLevels.get(componentName) || this.logLevel;
  }

  private shouldLog(messageLevel: LogLevel, effectiveLogLevel: LogLevel): boolean {
    const levels = Object.values(LogLevel);
    return levels.indexOf(messageLevel) >= levels.indexOf(effectiveLogLevel);
  }

  /** Debounce IndexedDB writes when auto-registering new components (Bug 4 fix) */
  private debouncedSave() {
    if (this.pendingSave) clearTimeout(this.pendingSave);
    this.pendingSave = setTimeout(() => {
      this.pendingSave = null;
      this.saveComponentLogLevels();
    }, 2000);
  }

  private async saveComponentLogLevels() {
    const levels: LoggingPreferences = {};
    this.componentLogLevels.forEach((value, key) => levels[key] = value);
    
    try {
      await this.indexedDbService.set('loggingPreferences', levels);
    } catch (error) {
      localStorage.setItem('loggingPreferences', JSON.stringify(levels));
    }
  }

  getLoggingPreferences(): { componentName: string, logLevel: LogLevel }[] {
    const preferences: { componentName: string, logLevel: LogLevel }[] = [];

    this.componentLogLevels.forEach((logLevel, componentName) => {
      preferences.push({ componentName, logLevel });
    });

    return preferences;
  }

  async updateLoggingPreferenceForComponent(componentName: string, newLogLevel: LogLevel) {
    this.componentLogLevels.set(componentName, newLogLevel);
    await this.saveComponentLogLevels();
  }
}
